import { cs } from '../../../lib/currency';
import { useState, useMemo } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Mail, Send, Loader2 } from 'lucide-react';
import api from '../../../services/api';
import { toast } from 'sonner';
import { invoiceKey, isReminderEligible } from './utils.jsx';

/**
 * Bulk / single payment-reminder confirmation dialog.
 *
 * Props:
 *   open, onOpenChange   — dialog control
 *   mode                 — 'single' | 'bulk'
 *   single               — invoice object when mode==='single'
 *   bulkInvoices         — pre-filtered bulk invoice list (from selectedIds)
 *   onSent               — callback after a clean run, parent should clear selection
 *   onRetainFailures     — bulk only: parent should reduce selection to failed ids
 */
export default function ReminderDialog({
  open, onOpenChange, mode, single, bulkInvoices, onSent, onRetainFailures,
}) {
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const items = useMemo(
    () => (mode === 'single' && single ? [single] : (bulkInvoices || [])),
    [mode, single, bulkInvoices],
  );
  const summary = useMemo(() => {
    const eligible = items.filter(isReminderEligible);
    const totalAmount = eligible.reduce((s, inv) => s + (Number(inv.amount) || 0), 0);
    const recipients = new Set(eligible.map((inv) => inv.parent_email).filter(Boolean));
    const byType = eligible.reduce((acc, inv) => {
      acc[inv.type_label || inv.type] = (acc[inv.type_label || inv.type] || 0) + 1;
      return acc;
    }, {});
    return {
      count: eligible.length,
      totalAmount,
      recipientCount: recipients.size,
      byType,
      skipped: items.length - eligible.length,
    };
  }, [items]);

  const handleClose = (open_) => {
    if (sending) return;
    if (!open_) setResult(null);
    onOpenChange(open_);
  };

  const send = async () => {
    setSending(true);
    try {
      const payload = items
        .filter(isReminderEligible)
        .map((inv) => ({
          id: inv.type === 'small_group' ? String(inv.id).replace(/^sg-/, '') : inv.id,
          type: inv.type,
        }));
      if (payload.length === 0) {
        toast.error('No eligible invoices to send');
        setSending(false);
        return;
      }

      const { data } = await api.post('/admin/invoices/send-payment-reminders', { invoices: payload });
      const sent = data?.sent ?? 0;
      const skipped = data?.skipped ?? 0;
      const failed = data?.failed ?? 0;
      const failures = Array.isArray(data?.failures) ? data.failures : [];
      setResult({ sent, skipped, failed, failures });

      if (sent > 0 && failed === 0) {
        toast.success(`Sent ${sent} reminder${sent === 1 ? '' : 's'}.${skipped ? ` ${skipped} skipped (paid / no email).` : ''}`);
      } else if (failed > 0) {
        toast.warning(`Sent ${sent}, ${failed} failed. See dialog for details.`);
      } else if (skipped > 0) {
        toast.warning(`No emails sent — ${skipped} skipped (already paid or missing email).`);
      } else {
        toast.error('No reminders were sent.');
      }

      if (failed === 0) {
        onOpenChange(false);
        setResult(null);
        onSent && onSent();
      } else if (mode === 'bulk') {
        onRetainFailures && onRetainFailures(failures);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to send reminders');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg" data-testid="reminder-confirm-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-amber-600" />
            {mode === 'single' ? 'Send Payment Reminder' : 'Send Payment Reminders'}
          </DialogTitle>
          <DialogDescription>
            Email the seeded "Invoice Reminder" template to the selected
            recipients. Paid or void invoices are skipped automatically.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <p className="text-slate-600">
            Uses the seeded <strong>"Invoice Reminder"</strong> email template. Already-paid
            or void invoices in the selection will be skipped automatically.
          </p>
          <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 space-y-1.5">
            <div className="flex justify-between">
              <span className="text-slate-600">Invoices</span>
              <span className="font-semibold" data-testid="reminder-count">{summary.count}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Unique recipients</span>
              <span className="font-semibold">{summary.recipientCount}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Total outstanding</span>
              <span className="font-semibold">{cs()}{summary.totalAmount.toFixed(2)}</span>
            </div>
            {summary.skipped > 0 && (
              <div className="flex justify-between text-xs text-amber-700">
                <span>Will be skipped (paid/void)</span>
                <span>{summary.skipped}</span>
              </div>
            )}
          </div>
          {Object.keys(summary.byType).length > 0 && (
            <div className="text-xs text-slate-500">
              <span className="font-medium text-slate-700">Breakdown:</span>{' '}
              {Object.entries(summary.byType).map(([t, c]) => `${t} (${c})`).join(' · ')}
            </div>
          )}
          {mode === 'single' && single && (
            <div className="text-xs text-slate-500 border-t pt-3">
              To: <span className="font-medium text-slate-700">{single.parent_email || '(no email)'}</span>
            </div>
          )}
          {result && result.failures && result.failures.length > 0 && (
            <div className="border-t pt-3" data-testid="reminder-failures-section">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-red-700">{result.failed} failed to deliver</span>
                <span className="text-xs text-slate-500">{result.sent} sent · {result.skipped} skipped</span>
              </div>
              <div className="max-h-44 overflow-y-auto border rounded-md divide-y">
                {result.failures.map((f, idx) => (
                  <div
                    key={`${f.type}-${f.id}-${idx}`}
                    className="px-3 py-2 text-xs"
                    data-testid={`failure-item-${idx}`}
                  >
                    <div className="flex justify-between gap-2">
                      <span className="font-medium text-slate-700 truncate">
                        {f.parent_name || f.parent_email || '(unknown recipient)'}
                      </span>
                      <span className="text-slate-400 capitalize">{f.type?.replace('_', ' ')}</span>
                    </div>
                    {f.parent_email && (
                      <div className="text-slate-500 truncate">{f.parent_email}</div>
                    )}
                    <div className="text-red-600 mt-0.5 break-words">{f.reason}</div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Tip: Click <strong>Retry failed</strong> to resend only to the recipients above.
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => { onOpenChange(false); setResult(null); }}
            disabled={sending}
            data-testid="reminder-cancel-btn"
          >
            {result && result.failed > 0 ? 'Close' : 'Cancel'}
          </Button>
          <Button
            onClick={send}
            disabled={sending || summary.count === 0}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="reminder-confirm-btn"
          >
            {sending ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending...</>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                {result && result.failed > 0 ? 'Retry failed' : (
                  <>Send {summary.count > 1 ? `${summary.count} ` : ''}Reminder
                  {summary.count !== 1 ? 's' : ''}</>
                )}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export { invoiceKey };
